import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { createSupplierNotice } from '../../../models/noticesModel';
import { toast } from 'react-toastify';
import WysiwygEditor from '../../../components/common/WysiwygEditor';

function SupplierNoticeCreate() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  
  const [formData, setFormData] = useState({
    notice_title: '',
    notice_text: '',
    notice_shutdown: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.notice_title.trim()) {
      toast.error('Notice title is required');
      return;
    }

    if (!formData.notice_text.trim()) {
      toast.error('Notice content is required');
      return;
    }

    try {
      setLoading(true);
      const submitData = {
        ...formData,
        files: files
      };
      
      const response = await createSupplierNotice(submitData);
      
      if (response.status === 1) {
        toast.success('Notice created successfully');
        navigate('/dashboard/notices/supplier');
      } else {
        throw new Error(response.message || 'Failed to create notice');
      }
    } catch (err) {
      console.error('Error creating notice:', err);
      toast.error(err.message || 'Failed to create notice');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = useCallback(() => {
    navigate('/dashboard/notices/supplier');
  }, [navigate]);

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header with Back Navigation */}
      <div className="mb-6">
        <button
          onClick={handleBack}
          className="flex items-center text-[#0087D4] hover:text-blue-600 transition-colors mb-4"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">Create Notice</h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Main Form */}
          <div className="lg:col-span-2">
            <div className="bg-white p-6 rounded-lg shadow border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Notice Details</h3>
              <div className="space-y-4">
                {/* Title */}
                <div>
                  <label htmlFor="notice_title" className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    id="notice_title"
                    name="notice_title"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm text-gray-900"
                    value={formData.notice_title}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter notice title"
                  />
                </div>

                {/* Date and Time (optional, hidden by default in legacy) */}
                <div id="shutdown" style={{ display: 'none' }}>
                  <label htmlFor="notice_shutdown" className="block text-sm font-medium text-gray-700 mb-1">
                    Date and Time
                  </label>
                  <input
                    type="text"
                    id="notice_shutdown"
                    name="notice_shutdown"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm text-gray-900"
                    value={formData.notice_shutdown}
                    onChange={handleInputChange}
                    placeholder="Date and time"
                  />
                </div>

                {/* Content */}
                <div>
                  <label htmlFor="notice_text" className="block text-sm font-medium text-gray-700 mb-2">
                    Content *
                  </label>
                  <div className="border border-gray-300 rounded-md" style={{ minHeight: '300px' }}>
                    <WysiwygEditor
                      value={formData.notice_text}
                      onChange={(content) => setFormData(prev => ({
                        ...prev,
                        notice_text: content
                      }))}
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <div>
                  <button 
                    type="submit" 
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#28a745] hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Attachments */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 rounded-lg shadow border">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Attachments</h4>
              <div className="space-y-4">
                <div>
                  <input
                    id="files"
                    name="files"
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Select one or more files to attach
                  </p>
                </div>
                
                {/* File List */}
                {files.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">Selected files:</p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {files.map((file, index) => (
                        <li key={index} className="flex items-center">
                          <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                          {file.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div id="feedback"></div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default SupplierNoticeCreate;
